/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import { Lightbox } from '../lib/lightbox.js';
import { isExternalLink } from '../lib/links.js';

/** The paywall overlay, once the limit has been reached. */
let lightbox = null;

/**
 * The overlay, built the first time something needs it.
 *
 * Three places used to build it, each behind `lightbox === undefined`.
 * That was a property of one shared object when the Map Viewer had one, and
 * a property nobody has set is `undefined`; a module-level binding
 * initialised to `null` never equals it, so none of the three ever built
 * anything and every one of them went on to call a method on `null`.
 *
 * @param {HTMLElement} mapElem - The map the overlay covers.
 * @param {function(HTMLElement|string): void} onShow - Wires up what the
 *   overlay is given: its links, its forms and its close control. Passed
 *   in rather than read from the module, where the name belongs to a
 *   binding nothing assigns.
 * @returns {object} The overlay.
 */
/**
 * Wire up what the overlay was given: its links, its forms, and the
 * control that closes it.
 *
 * At module scope because three callers need it and one of them runs
 * outside `init`. It reads nothing from there.
 *
 * @param {HTMLElement|string} content - The markup the overlay shows.
 */
const lightboxHandler = (content) => {
const root =
    typeof content === 'string'
        ? (() => {
              const d = document.createElement('div');
              d.innerHTML = content;
              return d;
          })()
        : content;

setTimeout(() => {
    root.querySelectorAll('.form__fieldsetMessages').forEach((n) => {
        n.classList.add('visible');
    });
}, 50);

// Links (except the close control)
root.querySelectorAll('a[href]').forEach((a) => {
    a.addEventListener('click', (ev) => {
        if (a.closest('.lightbox__close')) return;
        ev.preventDefault();
        fetch(a.href, { credentials: 'same-origin' })
            .then((r) => {
                if (!r.ok) throw r;
                return r.text();
            })
            .then((html) => {
                lightbox.show(html);
            })
            .catch(() => {
                console.error(
                    `Could not load content from "${a.href}"`
                );
            });
    });
});

// Forms → POST
root.querySelectorAll('form').forEach((form) => {
    form.addEventListener('submit', (ev) => {
        ev.preventDefault();
        fetch(form.action, {
            method: 'POST',
            body: new FormData(form),
            credentials: 'same-origin'
        })
            .then((r) => {
                if (r.status === 204) {
                    lightbox.close();
                    window.location.reload();
                    return null;
                }
                if (!r.ok) throw r;
                return r.text();
            })
            .then((html) => {
                if (html != null) lightbox.show(html);
            })
            .catch((e) => {
                console.error(
                    'Fetching content failed!',
                    e && `${e.status}: ${e.statusText}`
                );
            });
    });
});
};

function ensureLightbox(mapElem, onShow) {
    if (!lightbox) {
        lightbox = new Lightbox(mapElem, {
            header: document.getElementById('header'),
            onShow,
        });
    }
    return lightbox;
}
import { getPopupInstances } from '../lib/instances.js';
import { getConfig } from '../lib/config.js';
import { on } from '../lib/events.js';

/*
 * Subscribes through the event bus rather than jQuery.
 *
 * `emit` used to fan out to `jQuery(target).trigger(name)` purely so this
 * module and the paywall lightbox could listen that way. Neither reads the
 * event target, so a plain subscription is equivalent — and it is what let
 * that third channel, and jQuery with it, be removed.
 */

export function init() {
const mapElem = document.getElementById('map');
const loginURL = getConfig()?.endpoints?.meteredLogin || '';
const popupLimit = getConfig()?.metering?.popupLimit || 3;



const hashFormMatch = window.location.hash.match(
    /\bform=(\/[a-z0-9][/a-z0-9_.-]*)/i
);
// When URL hash requests a form overlay
if (hashFormMatch) {
    ensureLightbox(mapElem, lightboxHandler);
    const formURL = hashFormMatch[1];
    fetch(`${formURL}?overlay=1`, { credentials: 'same-origin' })
        .then((r) => {
            if (!r.ok) throw r;
            return r.text();
        })
        .then((html) => {
            lightbox.show(html);
        })
        .catch(() => {
            console.error(`Could not load form from "${formURL}"`);
        });
}

on('loaded.map', () => {
    const visitsKey = 'v';
    const popupsKey = 'p';
    const { panel: popupPanel, manager: popupManager } =
        getPopupInstances();
    if (!popupPanel && !popupManager) return;

    // The list of visit listeners is a global on purpose: it is the
    // extension point a customer's page registers against, and `stats.js`
    // reads it there rather than importing one. The paywall is one more
    // listener, so it registers in the same place. A module-local list
    // would be one nothing ever reads, and the wall would never appear.
    window.Kontextlab = window.Kontextlab || {};
    const stats = (window.Kontextlab.stats = window.Kontextlab.stats || {});
    stats.popupListeners = stats.popupListeners || [];
    stats.popupListeners.push(
        (mapId, mapStats, popupId, popupStats) => {
            const visitedPopups = [];
            Object.keys(mapStats[popupsKey]).forEach((pId) => {
                if (
                    mapStats[popupsKey][pId][visitsKey] &&
                    mapStats[popupsKey][pId][visitsKey] > 0
                ) {
                    visitedPopups.push(pId);
                }
            });
            let popupWasVisitedBefore = false; // ES6: let
            visitedPopups.forEach((pId) => {
                if (
                    pId === popupId &&
                    mapStats[popupsKey][pId][visitsKey] > 1
                ) {
                    popupWasVisitedBefore = true;
                }
            });
            if (
                visitedPopups.length > popupLimit &&
                !popupWasVisitedBefore
            ) {
                // Remove visit from stats again
                popupStats[visitsKey]--;
                // Hide popups
                popupPanel.hide(true);
                popupManager.hide(true);
                // Show overlay
                ensureLightbox(mapElem, lightboxHandler);
                fetch(`${loginURL}?overlay=1`, {
                    credentials: 'same-origin'
                })
                    .then((r) => {
                        if (!r.ok) throw r;
                        return r.text();
                    })
                    .then((html) => {
                        lightbox.show(html);
                    })
                    .catch((error) => {
                        // The error itself, not only the URL: this covers
                        // showing the form as well as fetching it, and
                        // reporting a load failure for a render failure
                        // sends the next reader looking at the network.
                        console.error(
                            `Could not show the login form from`
                                + ` "${loginURL}"`,
                            error
                        );
                    });
            }
        }
    );
});
}

document.addEventListener('DOMContentLoaded', () => {
    const mapElem = document.getElementById('map');
    const headerLinks = document.querySelectorAll(
        '#header nav a[href], #menu a[href]'
    );
    if (headerLinks.length === 0) return;

    function isLoginURL(url) {
        const isInternal = !isExternalLink(url);
        const endsWithLogin = /\/login$/.test(url);
        return isInternal && endsWithLogin;
    }
    headerLinks.forEach((link) => {
        if (!isLoginURL(link.href)) return;
        link.addEventListener('click', (event) => {
            event.preventDefault();
            ensureLightbox(mapElem, lightboxHandler);
            const loginURL = getConfig()?.endpoints?.meteredLogin || '';
            fetch(`${loginURL}?overlay=1`, { credentials: 'same-origin' })
                .then((r) => {
                    if (!r.ok) throw r;
                    return r.text();
                })
                .then((html) => {
                    lightbox.show(html);
                })
                .catch(() => {
                    console.error(
                        `Could not load login form from "${loginURL}"`
                    );
                });
        });
    });
});
